import { PermissionsResourceType, type User } from '@netpurpose/api'

export const checkInternalUser = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return !!user?.groups.find((group) => group.name === 'Net Purpose')
}

export const canListEstimations = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return user.hasPerm(PermissionsResourceType.EstimationsData, 'LIST')
}

export const canAccessPSIData = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return (
    user.hasPerm(PermissionsResourceType.EstimationsData, 'LIST') &&
    user.hasPerm(PermissionsResourceType.EstimationsData, 'GET')
  )
}

export const canAccessSDGRevenueAlignmentNPData = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return user.hasPerm(PermissionsResourceType.SDGRevenueAlignment, 'INCLUDE_NP_DATA')
}

export const canAccessSDGRevenueAlignmentData = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return (
    user.hasPerm(PermissionsResourceType.SDGRevenueAlignment, 'INCLUDE_NP_DATA') ||
    user.hasPerm(PermissionsResourceType.SDGRevenueAlignment, 'INCLUDE_FACTSET_DATA')
  )
}

export const canAccessSDIRevenueData = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return (
    user.hasPerm(PermissionsResourceType.SDIRevenue, 'GET') &&
    user.hasPerm(PermissionsResourceType.SDIRevenue, 'LIST')
  )
}

export const canAccessOutcomesData = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return (
    user.hasPerm(PermissionsResourceType.Outcomes, 'GET') &&
    user.hasPerm(PermissionsResourceType.Outcomes, 'LIST')
  )
}

export const canAccessSDILabelsData = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return (
    user.hasPerm(PermissionsResourceType.SDILabels, 'GET') &&
    user.hasPerm(PermissionsResourceType.SDILabels, 'LIST')
  )
}

// SDI Revenue metrics carry the same "SDG" display theme as SDG Revenue ones, so
// either permission has to open up the theme and its analysed KPIs — otherwise the
// SDI Revenue breakdown links through to a data tab that cannot show them.
export const canAccessSDGThemeMetrics = (user: User | undefined): boolean =>
  canAccessSDGRevenueAlignmentData(user) || canAccessSDIRevenueData(user)

export const canListTechnologies = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return user.hasPerm(PermissionsResourceType.Technologies, 'LIST')
}

export const canGetTechnologies = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return user.hasPerm(PermissionsResourceType.Technologies, 'GET')
}

export const canCreateMetricsConfigs = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return user.hasPerm(PermissionsResourceType.MetricsConfig, 'CREATE', '?')
}

export const canEditMetricsConfigs = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return user.hasPerm(PermissionsResourceType.MetricsConfig, 'EDIT', '?')
}

export const canDeleteMetricsConfigs = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return user.hasPerm(PermissionsResourceType.MetricsConfig, 'DELETE', '?')
}

export const canAccessWatchlists = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return user.hasPerm(PermissionsResourceType.Portfolio, 'ACCESS_WATCHLISTS')
}

export const canAccessExplorePage = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return user.hasPerm(PermissionsResourceType.AppFeatures, 'ACCESS_EXPLORE')
}

export const canGetAPIKey = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return user.hasPerm(PermissionsResourceType.AppFeatures, 'GET_API_KEY')
}

export const canAccessFundOfFunds = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return user.hasPerm(PermissionsResourceType.AppFeatures, 'ACCESS_FOF')
}

export const canResetDemoUser = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return user.hasPerm(PermissionsResourceType.AppFeatures, 'DEMO_RESET')
}

export const canAccessSnapshots = (user: User | undefined): boolean => {
  if (!user) {
    return false
  }
  return user.hasPerm(PermissionsResourceType.AppFeatures, 'ACCESS_SNAPSHOTS')
}
